import React, { FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { useJoin } from '../../contexts/JoinContext';
import JoinCheckboxFormGroup from './JoinCheckboxFormGroup';
import JoinOptionButtonGroup from './JoinOptionButtonGroup';
import JoinOptionButton from './JoinOptionButton';
import JoinActionButton from './JoinActionButton';
import JoinRadioButtonFormGroup from './JoinRadioButtonFormGroup';
import { ACCOUNT_TYPE_SUCCESSFUL_USER, BODY_TYPES, INTERESTS_OPTIONS } from './constants';
import { DashlineSeparator, VerticalSeparator, FlexContainer } from './styledElements';
import { Slider, Switch } from 'swui';
import styled from '@emotion/styled';
import { HEIGHTS_VALUES } from '../../config/constants';
import JoinSkipButton from './JoinSkipButton';
import { useFilteredBodyType, cmToFeetInches, convertCMToInchesHeight } from '../../utils/helpers';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { Account, AccountMaker } from '../../models/Account';
import { connect } from 'react-redux';
import { shouldShowHeignInFeet } from '../../common';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { RootState } from '../../store';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';

const SliderLabel: FC<{ isHeightInFt: boolean; isSuccessfulUser: boolean; children: React.ReactNode }> = ({
  isHeightInFt,
  isSuccessfulUser,
  children,
}) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {isHeightInFt ? (
        <>
          <span className="label" style={{ fontSize: 14 }}>{`<5'`}</span>
          {children}
          <span className="label" style={{ float: 'right', fontSize: 14 }}>
            {isSuccessfulUser ? `>7'` : `>6'`}
          </span>
        </>
      ) : (
        <>
          <span className="label" style={{ fontSize: 14 }}>{`< 152cm`}</span>
          {children}
          <span className="label" style={{ float: 'right', fontSize: 14 }}>
            {isSuccessfulUser ? `>214cm` : `>183cm`}
          </span>
        </>
      )}
    </div>
  );
};

const StyleSlider = styled('div')({
  marginTop: 0,
  top: -5,
  span: {
    backgroundColor: '#D9D9D9 !important',
  },
});

const SliderValue = styled('span')({
  fontSize: 14,
  fontWeight: 400,
});

interface JoinProfileDetailsProps {
  account: Account;
}
const JoinProfileDetails: FC<JoinProfileDetailsProps> = ({ account }): ReactElement => {
  const { isMobile } = useMediaBreakpoints();
  const {
    currentFormData,
    setCurrentFormData,
    nextStep,
    handleShowSkipModal,
    stepsFormData,
    selectedInterestsLabel,
    isMandatoryField,
    setSkippedStepCounter,
    currentStepIndex,
  } = useJoin();
  const [height, setHeight] = useState('0');
  const [defaultHeight, setDefaultHeight] = useState<any[]>(currentFormData.heightValue);
  const [isHeightInFt, setIsHeightInFt] = useState(
    shouldShowHeignInFeet(account.getData()?.profile?.data?.country) ?? currentFormData.heightIsFt
  );
  const [heightInCm, setHeightInCm] = useState(151);
  const [heightInFt, setHeightInFt] = useState('5');

  const filteredBodyType = useFilteredBodyType(stepsFormData[0].sex === 1 ? 'Male' : 'Female');

  const isSuccessfulUser = useMemo(
    () => stepsFormData[0].account_type === ACCOUNT_TYPE_SUCCESSFUL_USER,
    [stepsFormData]
  );

  const memoisedDefaultHeight = useMemo(() => currentFormData.heightValue, [currentFormData.heightValue]);

  const allowSubmit = useMemo(() => {
    const hasSelectedInterests = [
      currentFormData.dating_just_dating,
      currentFormData.dating_friendship,
      currentFormData.dating_marriage,
      currentFormData.dating_married,
      currentFormData.dating_adult,
      currentFormData.dating_mentorship,
    ].includes(true);

    const requiredFields = [currentFormData.body_type, defaultHeight[0]];

    const hasValue = isMandatoryField() // Call the isMandatoryField function instead of referencing it as a boolean value
      ? requiredFields.every(field => field !== '' && field !== null && field !== undefined)
      : requiredFields.some(field => field !== '' && field !== null && field !== undefined);

    return isMandatoryField() ? Boolean(hasValue && hasSelectedInterests) : Boolean(hasValue || hasSelectedInterests);
  }, [currentFormData, defaultHeight]);

  useEffect(() => {
    if (defaultHeight) {
      calculateHeight(defaultHeight[0]);
    }
  }, [defaultHeight, isHeightInFt]);

  const calculateHeight = (value: number): void => {
    setHeightInCm(value);

    const convertHeightInFt = convertCMToInchesHeight(value);
    const validHeight = convertHeightInFt ?? '0';

    if (isHeightInFt) {
      setHeight(validHeight);
    } else {
      setHeight(value ? value.toFixed(0) : '0');
    }

    setHeightInFt(convertHeightInFt);
  };

  const onHeightMeasurementChange = (value: boolean) => {
    setIsHeightInFt(value);
    setCurrentFormData({ heightIsFt: value });
  };

  const handleInterestChange = item => {
    const { value: key } = item;

    setCurrentFormData({
      ...currentFormData,
      [key]: !currentFormData[key],
    });
  };

  const isFormCompleted = () => {
    const bodyType = BODY_TYPES[currentFormData.body_type];

    return typeof bodyType !== 'undefined' && selectedInterestsLabel().length > 0;
  };

  const handleMixpanelEvent = (result?: string) => {
    if (!account?.isEligibleForMixpanelEvents()) {
      return;
    }

    if (!result) {
      result = isFormCompleted() ? 'Completed' : 'Skipped';
    }

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PROFILE_DETAILS_STEP_1_COMPLETED,
      payload: {
        eventProperties: {
          Result: result,
        },
      },
    });
  };

  const getHeightFormDataValue = () => {
    if (heightInCm > 213) {
      return `> 7'0" (> 214cm)`;
    }

    if (heightInCm < 153) {
      return `< 5'0" (<152cm)`;
    }

    return HEIGHTS_VALUES[heightInFt];
  };

  const handleNextStep = () => {
    handleMixpanelEvent();
    nextStep();
    setCurrentFormData({ height: getHeightFormDataValue(), heightValue: defaultHeight, heightIsFt: isHeightInFt });
    setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: false }));
  };

  return (
    <div>
      <div style={{ overflow: 'hidden' }}>
        {!isMobile ? <DashlineSeparator style={{ marginTop: 0 }} /> : <div style={{ marginTop: 15 }} />}
        <JoinRadioButtonFormGroup
          label="Body Type"
          subLabel={'(select one)'}
          inlineSubLabel={true}
          customInputLabelStyle={{ marginBottom: 7, fontSize: 15 }}
          isRequired={isMandatoryField()}
          customRadioListStyle={{ marginTop: 11 }}
        >
          <JoinOptionButtonGroup
            name="body_type"
            lists={filteredBodyType}
            value={currentFormData.body_type}
            onChangeHandler={item => {
              if (currentFormData.body_type === item.value) {
                return setCurrentFormData({
                  body_type: null,
                });
              }
              setCurrentFormData({ body_type: item.value });
            }}
            customButtonGroupStyle={{ display: 'flex', flexFlow: 'wrap', justifyContent: 'space-between' }}
            additionalStyle={{ minWidth: '39%', margin: '0 0 10px 0' }}
          />
        </JoinRadioButtonFormGroup>
      </div>
      <DashlineSeparator style={{ marginTop: 0 }} />
      <JoinCheckboxFormGroup
        customStyle={{ position: 'relative' }}
        label={'Height'}
        customContainerStyle={{ marginBottom: 8 }}
        isRequired={isMandatoryField()}
      >
        <StyleSlider className="switchSWUI">
          <Switch onText="in" offText="cm" checked={isHeightInFt} onChange={onHeightMeasurementChange} />
        </StyleSlider>
        <StyleSlider>
          <Slider
            min={152}
            max={isSuccessfulUser ? 214 : 183}
            defaultValue={memoisedDefaultHeight}
            onHandleChange={height => {
              setDefaultHeight(height);
            }}
          />
        </StyleSlider>
        <SliderLabel isHeightInFt={isHeightInFt} isSuccessfulUser={isSuccessfulUser}>
          <SliderValue>
            {
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              height === 0 ? '' : !isHeightInFt && height
            }
            {isHeightInFt ? cmToFeetInches(heightInCm) : 'cm'}
          </SliderValue>
        </SliderLabel>
      </JoinCheckboxFormGroup>
      <DashlineSeparator />
      <JoinCheckboxFormGroup
        label={'What are your dating intentions?'}
        subLabel={'(select all that apply)'}
        inlineSubLabel={false}
        customCheckboxListStyle={{ marginTop: 11, display: 'flex', flexFlow: 'wrap', justifyContent: 'space-between' }}
        isRequired={isMandatoryField()}
        customContainerStyle={{ marginBottom: 0 }}
      >
        {INTERESTS_OPTIONS.map((item, index) => (
          <JoinOptionButton
            name="interests"
            index={index}
            key={index}
            title={item.label}
            id={index + 1}
            animate={false}
            isSelected={currentFormData[item.value]}
            onChangeHandler={() => handleInterestChange(item)}
            additionalStyle={{ minWidth: '39%', margin: '0 0 10px 0' }}
          />
        ))}
      </JoinCheckboxFormGroup>
      <DashlineSeparator />
      <div style={{ textAlign: 'center' }}>
        <JoinActionButton
          label="Next"
          onClick={handleNextStep}
          isDisabled={!allowSubmit}
          buttonType={'primaryRed'}
          isDefault={true}
        />
        {!isMandatoryField() && (
          <FlexContainer>
            <VerticalSeparator>
              <JoinSkipButton
                label="Skip"
                onClick={() =>
                  handleShowSkipModal(() => {
                    handleMixpanelEvent('Skipped');
                    nextStep();
                    setCurrentFormData({ height: getHeightFormDataValue(), heightValue: defaultHeight });
                    setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: true }));
                  })
                }
              />
            </VerticalSeparator>
          </FlexContainer>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    account: AccountMaker.create(state.profile),
  };
};

export default connect(mapStateToProps, {})(JoinProfileDetails);
